// Anomaly engine — pure, dependency-free, shared verbatim with the frontend
// (frontend/src/components/command/anomaly.js is a byte copy: the flags the
// Simulate mode shows come from the same code that runs on real data).
//
// Input: per-slug aligned series at one bucket size (`step` ms):
//   series[slug] = { ts: number[], req: number[], s5xx: number[], p95: number[], tokens: number[] }
// Baseline per (slug, metric): hour-of-day medians over the earlier part of
// the window when it spans >= 3 days, else a robust median/MAD over everything
// but the tail. A point is anomalous when its robust z >= `z` for
// `consecutive` buckets AND the value clears an absolute floor (so a quiet
// app going 1 -> 4 req/min is not a spike). Rules: traffic spike / drop, 5xx
// rate, p95, tokens spike, idle, cascade (two anomalies on flow-linked slugs
// within `cascadeMs`). Thresholds come from config/app apps.anomaly on the
// control plane; the frontend passes the same object.

export const DEFAULT_ANOMALY = {
  z: 3,
  consecutive: 3,
  floors: { req: 20, s5xxRate: 0.01, s5xxMinReq: 10, p95Ms: 400, tokens: 2000 },
  idleMs: 24 * 3600e3,
  cascadeMs: 5 * 60e3,
  severeZ: 6,
  severeS5xxRate: 0.05,
};

const median = (a) => { if (!a.length) return 0; const s = [...a].sort((x, y) => x - y); const m = s.length >> 1; return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };
export function robustStats(values) {
  const med = median(values);
  const mad = median(values.map((v) => Math.abs(v - med)));
  // Scale guard: a very smooth series must not turn a +20% drift into z 10 —
  // spread is at least 15% of the median (so z 3 ≈ 1.45× baseline) and never 0.
  const scale = Math.max(1.4826 * mad, med * 0.15, 1e-6);
  return { median: med, mad, scale };
}

// Baseline value for bucket i: same-hour median when the window is long
// enough, else the global robust median (computed once).
function baselineFor(ts, values, step, tailN) {
  const n = values.length;
  const spanMs = n * step;
  const head = values.slice(0, Math.max(1, n - tailN));
  const global = robustStats(head);
  if (spanMs < 3 * 24 * 3600e3) return () => global;
  const byHour = new Map();
  for (let i = 0; i < head.length; i += 1) {
    const h = new Date(ts[i]).getUTCHours();
    if (!byHour.has(h)) byHour.set(h, []);
    byHour.get(h).push(values[i]);
  }
  const cache = new Map();
  return (i) => {
    const h = new Date(ts[i]).getUTCHours();
    if (!cache.has(h)) { const arr = byHour.get(h); cache.set(h, arr && arr.length >= 3 ? robustStats(arr) : global); }
    return cache.get(h);
  };
}

// Scan one metric; returns runs of consecutive flagged buckets.
function runs(ts, values, { z, consecutive, floor, direction = 'up', step }) {
  const n = values.length;
  if (n < consecutive + 2) return [];
  const base = baselineFor(ts, values, step, Math.max(consecutive * 2, Math.round(n * 0.1)));
  const out = [];
  let run = null;
  for (let i = 0; i < n; i += 1) {
    const b = base(i);
    // A silent baseline (median 0, e.g. a quiet app's p95) must not print
    // z in the billions: spread is at least a quarter of the floor.
    const zi = (values[i] - b.median) / Math.max(b.scale, floor * 0.25);
    const hit = direction === 'up'
      ? zi >= z && values[i] >= floor
      : zi <= -z && b.median >= floor;
    if (hit) {
      if (!run) run = { start: i, end: i, peak: values[i], peakZ: Math.abs(zi), baseline: b.median };
      else { run.end = i; if (Math.abs(zi) > run.peakZ) { run.peakZ = Math.abs(zi); run.peak = values[i]; run.baseline = b.median; } }
    } else if (run) { out.push(run); run = null; }
  }
  if (run) out.push(run);
  // A run touching the left edge has an unknown onset (it may be the tail
  // of something already reported last window) — skip it.
  return out.filter((r) => r.start > 0 && r.end - r.start + 1 >= consecutive);
}

const fmt = (v, unit) => (unit === '%' ? `${(v * 100).toFixed(1)}%` : unit === 'ms' ? `${Math.round(v)} ms` : `${Math.round(v).toLocaleString()}${unit ? ` ${unit}` : ''}`);

export function detectAnomalies(series, { flows = [], now = Date.now(), step = 60e3, thresholds = DEFAULT_ANOMALY, usageDays = {} } = {}) {
  const T = { ...DEFAULT_ANOMALY, ...thresholds, floors: { ...DEFAULT_ANOMALY.floors, ...(thresholds?.floors || {}) } };
  // `consecutive` is in minutes; at coarser buckets one bucket already
  // spans it (5-min buckets → 1, hourly → 1).
  const consecutive = Math.max(1, Math.ceil((T.consecutive * 60e3) / step));
  const out = [];
  const push = (slug, metric, kind, r, ts, unit, severity) => {
    const open = r.end >= ts.length - 1;
    out.push({
      id: `${slug}:${metric}:${ts[r.start]}`,
      slug, metric, kind,
      value: r.peak, baseline: r.baseline, z: Math.round(r.peakZ * 10) / 10,
      severity: severity || (r.peakZ >= T.severeZ ? 'down' : 'warn'),
      openedAt: ts[r.start], closedAt: open ? null : ts[r.end] + step,
      text: `${slug} · ${metric} ${kind} · ${fmt(r.peak, unit)} vs ${fmt(r.baseline, unit)} baseline · z ${Math.round(r.peakZ * 10) / 10}`,
    });
  };

  for (const [slug, s] of Object.entries(series || {})) {
    const ts = s.ts || [];
    if (!ts.length) continue;
    const opts = { z: T.z, consecutive, step };
    if (s.req) {
      for (const r of runs(ts, s.req, { ...opts, floor: T.floors.req, direction: 'up' })) push(slug, 'traffic', 'spike', r, ts, 'req/min');
      for (const r of runs(ts, s.req, { ...opts, floor: T.floors.req, direction: 'down' })) push(slug, 'traffic', 'drop', r, ts, 'req/min');
    }
    if (s.s5xx && s.req) {
      const rate = s.s5xx.map((v, i) => (s.req[i] >= T.floors.s5xxMinReq ? v / s.req[i] : 0));
      for (const r of runs(ts, rate, { ...opts, floor: T.floors.s5xxRate, direction: 'up' })) push(slug, '5xx', 'rate', r, ts, '%', r.peak >= T.severeS5xxRate ? 'down' : undefined);
    }
    if (s.p95) for (const r of runs(ts, s.p95, { ...opts, floor: T.floors.p95Ms, direction: 'up' })) push(slug, 'p95', 'latency', r, ts, 'ms');
    if (s.tokens) for (const r of runs(ts, s.tokens, { ...opts, floor: T.floors.tokens, direction: 'up' })) push(slug, 'usage', 'spike', r, ts, 'tokens');
    // Idle: nothing in the last idleMs while the earlier window had traffic.
    if (s.req && ts[ts.length - 1] - ts[0] >= T.idleMs * 1.5) {
      const cut = now - T.idleMs;
      let recent = 0; let earlier = 0;
      for (let i = 0; i < ts.length; i += 1) { if (ts[i] >= cut) recent += s.req[i]; else earlier += s.req[i]; }
      if (recent === 0 && earlier > 0) out.push({ id: `${slug}:idle:${cut}`, slug, metric: 'traffic', kind: 'idle', value: 0, baseline: earlier, z: 0, severity: 'warn', openedAt: cut, closedAt: null, text: `${slug} · idle · no requests for 24h after ${Math.round(earlier).toLocaleString()} earlier in the window` });
    }
  }
  // Idle from the daily usage snapshots when no traffic series exists.
  for (const [slug, days] of Object.entries(usageDays || {})) {
    if (series?.[slug]?.req) continue;
    const dates = Object.keys(days || {}).sort();
    if (dates.length < 3) continue;
    const last = dates[dates.length - 1];
    const lastMs = Date.parse(`${last}T00:00:00Z`);
    if (now - lastMs > T.idleMs * 2) out.push({ id: `${slug}:idle:${last}`, slug, metric: 'usage', kind: 'idle', value: 0, baseline: dates.length, z: 0, severity: 'warn', openedAt: lastMs + 86400e3, closedAt: null, text: `${slug} · idle · last metered activity ${last}` });
  }

  // Cascade: two anomalies on flow-linked slugs opening within cascadeMs.
  const edges = new Set();
  for (const f of flows) { edges.add(`${f.from}>${f.to}`); }
  const open = out.filter((a) => a.kind !== 'idle');
  for (let i = 0; i < open.length; i += 1) {
    for (let j = 0; j < open.length; j += 1) {
      const a = open[i]; const b = open[j];
      if (a.slug === b.slug || !edges.has(`${a.slug}>${b.slug}`)) continue;
      const dt = b.openedAt - a.openedAt;
      if (dt < 0 || dt > T.cascadeMs) continue;
      const id = `cascade:${a.slug}>${b.slug}:${a.openedAt}`;
      if (out.some((x) => x.id === id)) continue;
      out.push({ id, slug: b.slug, from: a.slug, metric: 'flow', kind: 'cascade', value: b.value, baseline: b.baseline, z: Math.max(a.z, b.z), severity: 'down', openedAt: a.openedAt, closedAt: a.closedAt && b.closedAt ? Math.max(a.closedAt, b.closedAt) : null, text: `${a.slug} → ${b.slug} · cascade · ${a.metric} ${a.kind} followed by ${b.metric} ${b.kind} ${Math.round(dt / 60e3)} min later` });
    }
  }
  out.sort((a, b) => (b.closedAt == null) - (a.closedAt == null) || b.openedAt - a.openedAt);
  return out;
}

export const openAnomalies = (list) => (list || []).filter((a) => a.closedAt == null);
